import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { toast } from "sonner";
import { Wallet, ArrowDownCircle, ArrowUpCircle, Paperclip, X, ExternalLink, ArrowRight, FileDown } from "lucide-react";

const TOPUP_PRESETS = [1000, 2500, 5000, 10000, 25000];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Helpers for statement quarter chooser
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_Q = Math.floor(new Date().getMonth() / 3) + 1;

/**
 * Parent-side Ward Wallet modal — READ-ONLY history + top-up via Razorpay.
 *
 * Balance, transaction list with bill attachments and preset top-up buttons.
 * On successful Razorpay payment the wallet is auto-credited (backend webhook).
 */
export default function ParentWardWallet({ wardId, wardName, onClose }) {
  const [state, setState] = useState(null);
  const [amount, setAmount] = useState(2500);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [showStmt, setShowStmt] = useState(false);
  const [stmtYear, setStmtYear] = useState(CURRENT_YEAR);
  const [stmtQ, setStmtQ] = useState(CURRENT_Q);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { load(); }, [wardId]);
  const load = async () => {
    try { const { data } = await api.get(`/parent/wards/${wardId}/wallet`); setState(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const url = `${BACKEND}/api/parent/wards/${wardId}/wallet/statement.pdf?year=${stmtYear}&quarter=${stmtQ}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = `wallet-${wardName.replace(/\s+/g, "_")}-Q${stmtQ}_${stmtYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Statement Q${stmtQ} ${stmtYear} downloaded`);
      setShowStmt(false);
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setDownloading(false);
    }
  };

  const topUp = async () => {
    const finalAmount = custom ? parseInt(custom) || 0 : amount;
    if (finalAmount < 100) { toast.error("Minimum top-up is ₹100"); return; }
    setBusy(true);
    try {
      await startCheckout({
        kind: "wallet_topup",
        ref_id: wardId,
        amount: finalAmount,
        return_to: "/parent",
        onSuccess: async () => {
          toast.success(`₹${finalAmount.toLocaleString("en-IN")} added to ${wardName}'s wallet`);
          setBusy(false); setCustom("");
          // wait briefly for webhook then reload
          setTimeout(load, 1500);
        },
        onFailure: () => setBusy(false),
        onDismiss: () => setBusy(false),
      });
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  if (!state) return null;
  const bal = state.summary?.balance ?? 0;
  const finalAmount = custom ? parseInt(custom) || 0 : amount;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0B1120] border border-white/12 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="parent-wallet-modal">
        <div className="sticky top-0 bg-[#0B1120] border-b border-white/12 px-5 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="brand-orange" size={22} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Ward Wallet</div>
              <div className="font-display text-lg font-black uppercase tracking-tight">{wardName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowStmt((s) => !s)}
              data-testid="parent-wallet-statement-toggle"
              className="hidden sm:flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest border border-white/25 px-3 py-1.5 hover:bg-white/10"
            >
              <FileDown size={12} /> Statement
            </button>
            <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="parent-wallet-close"><X size={20} /></button>
          </div>
        </div>

        {showStmt && (
          <div className="mx-5 md:mx-6 mt-4 border border-brand-blue/40 bg-brand-blue/5 p-4" data-testid="statement-picker">
            <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-2">// Quarterly wallet statement · PDF</div>
            <div className="text-xs text-white/60 mb-3">
              Pick a quarter — every credit, debit and bill flag for that period will be on one branded page.
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={stmtYear}
                onChange={(e) => setStmtYear(Number(e.target.value))}
                data-testid="statement-year"
                className="bg-transparent border border-white/20 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"
              >
                {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
                  <option key={y} value={y} className="bg-[#0B1120]">{y}</option>
                ))}
              </select>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((q) => (
                  <button
                    key={q}
                    onClick={() => setStmtQ(q)}
                    data-testid={`statement-q${q}`}
                    className={`text-xs font-bold px-3 py-2 border ${stmtQ === q ? "border-brand-blue bg-brand-blue text-white" : "border-white/20 hover:bg-white/10"}`}
                  >
                    Q{q}
                  </button>
                ))}
              </div>
              <button
                onClick={downloadStatement}
                disabled={downloading}
                data-testid="statement-download"
                className="ml-auto bg-brand-blue text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {downloading ? "Preparing…" : <><FileDown size={12} /> Download PDF</>}
              </button>
            </div>
          </div>
        )}

        {/* Balance card */}
        <div className={`m-5 md:m-6 p-6 border ${bal >= 0 ? "border-brand-orange/50 bg-brand-orange/10" : "border-red-500/50 bg-red-500/10"}`} data-testid="parent-wallet-balance">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">Current Balance</div>
          <div className={`font-display text-4xl font-black ${bal >= 0 ? "brand-orange" : "text-red-400"}`}>
            ₹{Number(bal).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs font-mono text-white/60">
            <span>Total deposits: <span className="text-white">₹{Number(state.summary?.total_credits || 0).toLocaleString("en-IN")}</span></span>
            <span>Total spent: <span className="text-white">₹{Number(state.summary?.total_debits || 0).toLocaleString("en-IN")}</span></span>
          </div>
        </div>

        {/* Top-up */}
        <div className="mx-5 md:mx-6 mb-6 bg-[#131A2B] border border-white/12 p-5" data-testid="parent-wallet-topup">
          <div className="font-display text-lg uppercase font-bold brand-orange mb-1">Top up wallet</div>
          <p className="text-xs text-white/60 mb-3">Pay securely via Razorpay (UPI, cards, netbanking). Amount will reflect within a minute.</p>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-3">
            {TOPUP_PRESETS.map((v) => (
              <button key={v} onClick={() => { setAmount(v); setCustom(""); }}
                data-testid={`topup-preset-${v}`}
                className={`py-2.5 font-display font-black text-sm border ${amount === v && !custom ? "border-brand-orange bg-brand-orange/15 brand-orange" : "border-white/20 text-white/80 hover:border-white/40"}`}>
                ₹{v.toLocaleString("en-IN")}
              </button>
            ))}
          </div>
          <div className="flex items-center border border-white/20 mb-3">
            <span className="px-3 text-white/50 font-mono">₹</span>
            <input type="number" min="100" value={custom} onChange={(e) => setCustom(e.target.value)}
              placeholder="Custom amount" data-testid="topup-custom"
              className="flex-1 bg-transparent py-2.5 pr-3 outline-none text-sm" />
          </div>
          <button onClick={topUp} disabled={busy || finalAmount < 100} data-testid="topup-pay"
            className="w-full bg-brand-orange text-black py-3 font-bold uppercase tracking-widest text-sm hover:glow-orange disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? "Opening payment…" : (<>Pay ₹{finalAmount.toLocaleString("en-IN")} <ArrowRight size={14} /></>)}
          </button>
        </div>

        {/* Transactions */}
        <div className="mx-5 md:mx-6 mb-8">
          <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3">Transaction History</div>
          <div className="border border-white/12 divide-y divide-white/8" data-testid="parent-wallet-txns">
            {state.transactions.length === 0 && (
              <div className="p-8 text-center text-white/40 text-sm">No transactions yet.</div>
            )}
            {state.transactions.map((t) => (
              <div key={t.id} className="p-4 flex items-start gap-3" data-testid={`parent-txn-${t.id}`}>
                <div className={`h-9 w-9 flex items-center justify-center shrink-0 ${t.type === "credit" ? "bg-green-500/15" : "bg-red-500/15"}`}>
                  {t.type === "credit" ? <ArrowDownCircle size={16} className="text-green-400" /> : <ArrowUpCircle size={16} className="text-red-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="font-medium text-sm">{t.description}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{t.category}</div>
                  </div>
                  <div className="text-[11px] text-white/40 mt-1">
                    {new Date(t.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  {t.attachment_url && (
                    <a href={t.attachment_url} target="_blank" rel="noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-[11px] brand-blue hover:underline">
                      <Paperclip size={11} /> View bill <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                <div className={`text-right shrink-0 font-mono font-bold ${t.type === "credit" ? "text-green-400" : "text-red-400"}`}>
                  {t.type === "credit" ? "+" : "−"}₹{Number(t.amount).toLocaleString("en-IN")}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
